import { useAuth } from '@/app/_auth/auth-hooks'
import { useDomainIdQueryState } from '@/app/_common/domain/use-domain-from-url'
import { taskManagerApiPaths } from '@/hooks/api/_paths'
import { useApiRequestWithoutDomain } from '@/lib/http-utils'
import { useQuery, UseQueryOptions } from '@tanstack/react-query'

type DomainData = { externalId: string; id: string; name: string }
type DomainsApiResponse = {
  data: DomainData[]
}

function useDomainsQuery(
  options: Omit<
    UseQueryOptions<DomainsApiResponse['data']>,
    'queryKey' | 'queryFn' | 'select'
  > = {},
) {
  const { user } = useAuth()
  const { apiRequest } = useApiRequestWithoutDomain()
  const userEmail = user?.email
  const { domainId } = useDomainIdQueryState()

  return useQuery({
    queryKey: ['domains', userEmail, domainId],
    queryFn: async () => {
      const response = await apiRequest({
        path: taskManagerApiPaths.domains,
      })

      if (!response.ok) throw new Error('FAILED_TO_FETCH_DOMAINS')

      const data = (await response.json()) as DomainsApiResponse

      if (data?.data?.length > 0) {
        return data?.data?.map((domain) => {
          const externalId = domain?.externalId
          let name = domain?.name

          switch (externalId) {
            case 'em-orchestrator':
              name = 'em-platform'
              break
            case 'em-compliance':
              name = 'em-image-moderation'
              break
            default:
              break
          }
          return { ...domain, name }
        })
      }

      throw new Error('EMPTY_DOMAINS_DATA')
    },
    enabled: Boolean(userEmail),
    staleTime: Infinity,
    ...options,
  })
}

export { useDomainsQuery, type DomainsApiResponse }
